import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarningIcon from '@mui/icons-material/Warning';
import queryString from 'query-string';
import { CreatePhysicalProductRequest } from '../../common/model/dto/physical-product/create-physical-product-request';
import { OrderBy } from '../../common/types';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import { getListRange } from '../../common/utils/services/order-list-utils';
import { PHYSICAL_PRODUCT_LIST_FILTERS } from '../../common/model/ui/physical-product-list-filters';
import { useAdminPhysicalProductMutations } from '../../hooks/use-admin-physical-product-mutations';
import { useListQueryParams } from '../../hooks/use-list-query-params';
import { getPhysicalProducts } from '../../services/admin/admin-physical-product.service';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvCreatePhysicalProductDialog from '../UI/molecules/OvCreatePhysicalProductDialog';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvListFilterBar from '../UI/organisms/OvListFilterBar';
import OvPhysicalProductList from '../UI/organisms/OvPhysicalProductList';
import OvRecordListPage from '../UI/organisms/OvRecordListPage';

export const PHYSICAL_PRODUCT_LIST_LIMIT = 10;
const DEFAULT_ORDER: OrderBy = {
  order_by: 'created_at',
  order_dir: 'desc',
};

const AdminPhysicalProductList: FC = () => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    offset,
    orderBy,
    filters,
    filterQueryParams,
    hasAppliedFilters,
    changeFilter,
    clearFilters,
    setOffset,
  } = useListQueryParams(PHYSICAL_PRODUCT_LIST_FILTERS, DEFAULT_ORDER);

  const query = queryString.stringify({
    limit: PHYSICAL_PRODUCT_LIST_LIMIT,
    offset,
    order_by: orderBy?.order_by,
    order_dir: orderBy?.order_dir,
    ...filterQueryParams,
  });

  const {
    data: paginatedRecords,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['admin-physical-products', query],
    queryFn: () => getPhysicalProducts(query),
    placeholderData: keepPreviousData,
  });

  const records = paginatedRecords?.docs ?? [];
  const total = paginatedRecords?.total ?? 0;

  const { createProduct } = useAdminPhysicalProductMutations();

  const handleOnSave = (request: CreatePhysicalProductRequest) => {
    setIsDialogOpen(false);
    createProduct.mutate(request);
  };

  const getResultSummary = () => {
    if (isError) {
      return t('common.listResults.unavailable');
    }

    if (isPending) {
      return t('common.listResults.loading');
    }

    return t('physicalProducts.resultRange', {
      ...getListRange(offset, PHYSICAL_PRODUCT_LIST_LIMIT, total),
      total,
    });
  };

  const renderContent = () => {
    if (isError) {
      return (
        <OvListStateMessage
          isAlert
          icon={<WarningIcon />}
          title={t('physicalProducts.states.errorTitle')}
          description={t('physicalProducts.states.errorDescription')}
        >
          <OvCompactButton onClick={() => refetch()}>
            {t('common.actions.retry')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    if (isPending) {
      return null;
    }

    if (records.length > 0) {
      return <OvPhysicalProductList physicalProducts={records} />;
    }

    if (hasAppliedFilters) {
      return (
        <OvListStateMessage
          icon={<FilterListIcon />}
          title={t('physicalProducts.states.noResultsTitle')}
          description={t('physicalProducts.states.noResultsDescription')}
        >
          <OvCompactButton onClick={clearFilters}>
            {t('common.actions.clearFilters')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    return (
      <OvListStateMessage
        icon={<StorefrontIcon />}
        title={t('physicalProducts.states.emptyTitle')}
        description={t('physicalProducts.emptyPhysicalProductList')}
      />
    );
  };

  return (
    <OvRecordListPage
      title={t('app.subPages.menu.physicalProducts')}
      icon={<StorefrontIcon />}
      headerAction={
        <OvCompactButton
          icon={<AddCircleIcon />}
          onClick={() => setIsDialogOpen(true)}
        >
          {t('common.actions.create')}
        </OvCompactButton>
      }
      filters={
        <OvListFilterBar
          filters={filters}
          onFilterChange={changeFilter}
          onClearFilters={clearFilters}
        />
      }
      context={
        createProduct.error && (
          <OvErrorMessage message={getAxiosErrorMessage(createProduct.error)} />
        )
      }
      resultSummary={getResultSummary()}
      total={total}
      limit={PHYSICAL_PRODUCT_LIST_LIMIT}
      offset={offset}
      onPageChange={(_, page) =>
        setOffset((page - 1) * PHYSICAL_PRODUCT_LIST_LIMIT)
      }
      isBusy={isFetching || createProduct.isPending}
    >
      {renderContent()}

      <OvCreatePhysicalProductDialog
        isOpen={isDialogOpen}
        onCancel={() => setIsDialogOpen(false)}
        onSave={handleOnSave}
      />
    </OvRecordListPage>
  );
};

export default AdminPhysicalProductList;
